"use client";

import Link from "next/link";
import { Package } from "lucide-react";

import type { ProductListItem } from "@/shared/api/services/productService";
import type { PromotionProductItem } from "@/shared/api/services/promotionService";

import { useAutumnNewArrivals } from "@/features/promotions/queries/usePromotions";
import { ProductCard } from "@/features/shopping";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

function toProductListItem(p: PromotionProductItem): ProductListItem {
  return {
    id: p.id,
    name: p.name,
    idBrand: p.idBrand,
    imagePath: p.imagePath,
    goodsPrice: p.goodsPrice,
    fixedPrice: p.fixedPrice,
    "Brand.name": p.brandName,
  };
}

export function AutumnNewArrivalsContent() {
  const { data, isLoading, isError } = useAutumnNewArrivals();
  const entries = data?.data?.brands ?? [];

  return (
    <div className="max-w-screen-xl mx-auto px-4 pb-16">
      <section className="relative overflow-hidden rounded-lg mt-6">
        <div className="aspect-[2.5/1]">
          <img
            src="/banners/autumn-new-arrivals-2026.webp"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      <section className="mt-10 flex flex-col gap-4 border-b border-neutral-200 pb-10">
        <div className="text-xs text-neutral-500 tracking-wider">FW 26 · NEW ARRIVALS</div>
        <h1 className="text-3xl md:text-4xl font-heading font-semibold leading-tight text-neutral-900">
          가을, 새로 들어온
          <br />
          브랜드를 만나세요
        </h1>
        <p className="text-sm md:text-base text-neutral-600 leading-relaxed max-w-2xl">
          최근 입점한 신규 브랜드를 가장 먼저 소개합니다. 감도 높게 선별된 브랜드와 시즌 신상을
          매장에 먼저 들여보세요.
        </p>
      </section>

      {isLoading && (
        <section className="mt-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="border border-neutral-200 rounded-md p-4">
                <Skeleton className="aspect-square w-full mb-3" />
                <Skeleton className="h-3 w-12 mb-1" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            ))}
          </div>
        </section>
      )}

      {isError && (
        <section className="mt-12 flex flex-col items-center gap-4 text-center py-20">
          <Package className="h-12 w-12 text-neutral-300" strokeWidth={1} />
          <p className="text-sm text-neutral-600">신규 브랜드를 불러올 수 없습니다.</p>
          <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
            다시 시도
          </Button>
        </section>
      )}

      {!isLoading && !isError && entries.length === 0 && (
        <section className="mt-12 flex flex-col items-center gap-4 text-center py-20">
          <Package className="h-12 w-12 text-neutral-300" strokeWidth={1} />
          <p className="text-sm text-neutral-600">현재 소개할 신규 브랜드가 없습니다.</p>
          <Button variant="outline" size="sm" asChild>
            <Link href="/shopping">전체 상품 둘러보기</Link>
          </Button>
        </section>
      )}

      {entries.length > 0 && (
        <section className="mt-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {entries.map(({ brand }, i) => (
              <a
                key={brand.id}
                href={`#brand-${brand.id}`}
                className="group border border-neutral-200 rounded-md p-4 hover:border-neutral-900 transition-colors"
              >
                <div className="aspect-square w-full overflow-hidden bg-neutral-100 rounded-sm mb-3">
                  {brand.thumbnail ? (
                    <img
                      src={brand.thumbnail}
                      alt={brand.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-neutral-400 text-xs">
                      {brand.name}
                    </div>
                  )}
                </div>
                <span className="text-[11px] text-neutral-500 tracking-wider">NEW 0{i + 1}</span>
                <p className="mt-1 text-sm font-medium text-neutral-900 truncate">{brand.name}</p>
              </a>
            ))}
          </div>
        </section>
      )}

      {entries.map(({ brand, products }, i) => (
        <section key={brand.id} id={`brand-${brand.id}`} className="mt-16 scroll-mt-24 first:mt-12">
          <div className="flex items-end gap-6 border-b border-neutral-200 pb-6 mb-6">
            <Link
              href={`/brands/${brand.id}`}
              className="flex-shrink-0 w-24 h-24 md:w-28 md:h-28 rounded-sm overflow-hidden bg-neutral-100 block"
            >
              {brand.thumbnail ? (
                <img
                  src={brand.thumbnail}
                  alt={brand.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-neutral-400 text-xs">
                  {brand.name}
                </div>
              )}
            </Link>
            <div className="flex-1 min-w-0">
              <span className="text-xs text-neutral-500 tracking-wider">NEW BRAND 0{i + 1}</span>
              <h2 className="mt-1 text-2xl md:text-3xl font-heading font-semibold text-neutral-900 truncate">
                {brand.name}
              </h2>
            </div>
            <Link
              href={`/brands/${brand.id}`}
              className="hidden sm:inline-block text-xs text-neutral-600 hover:text-neutral-900 hover:underline flex-shrink-0"
            >
              브랜드 전체보기
            </Link>
          </div>

          {products.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <Package className="h-8 w-8 text-neutral-300" strokeWidth={1} />
              <p className="text-sm text-neutral-500">등록된 상품이 아직 없습니다</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {products.map((p, j) => (
                <ProductCard
                  key={p.id}
                  product={toProductListItem(p)}
                  priority={i === 0 && j < 2}
                />
              ))}
            </div>
          )}
        </section>
      ))}

      {entries.length > 0 && (
        <section className="mt-20 border-t border-neutral-200 pt-10 flex flex-col items-center gap-4 text-center">
          <p className="text-sm text-neutral-600">더 많은 신상을 둘러보고 싶으신가요?</p>
          <Button asChild size="lg" className="rounded-sm">
            <Link href="/shopping">전체 상품 둘러보기</Link>
          </Button>
        </section>
      )}
    </div>
  );
}

export const SpringCollectionContent = AutumnNewArrivalsContent;
